/**
 * The constructor's glyphs (DESIGN §9.3).
 *
 * The editor used to say "move this up" with a `↑` and "delete this" with a
 * `✕`: text pretending to be an icon, which lands at whatever size and weight
 * the font feels like. These are drawn on the same 24-unit grid as the
 * dashboard's own set — `components/icons.tsx` lends the frame — so a block
 * type in the palette, in a list row and in the inspector's header is one
 * picture at three sizes rather than three drawings.
 */

import { Svg, type IconProps } from "../components/icons";
import type { BlockType } from "./spec";

/* ── Chrome: the toolbar and the row actions ───────────────────────────── */

/** Back to the list of designs. */
export function IconBack(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Svg>
  );
}

export function IconUndo(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.5 9h9.8a4.7 4.7 0 0 1 0 9.4H8.6" />
      <path d="M8.2 4.6 3.8 9l4.4 4.4" />
    </Svg>
  );
}

export function IconRedo(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M19.5 9H9.7a4.7 4.7 0 0 0 0 9.4h5.7" />
      <path d="m15.8 4.6 4.4 4.4-4.4 4.4" />
    </Svg>
  );
}

/** Everything that did not earn a place in the toolbar. */
export function IconMore(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="5.2" cy="12" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="18.8" cy="12" r="1.35" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function IconDuplicate(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2.4" />
      <path d="M15.6 8.5V6.4A2.4 2.4 0 0 0 13.2 4H6.4A2.4 2.4 0 0 0 4 6.4v6.8a2.4 2.4 0 0 0 2.4 2.4h2.1" />
    </Svg>
  );
}

export function IconTrash(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4.6 6.8h14.8" />
      <path d="M9.6 6.8V5.3a1.3 1.3 0 0 1 1.3-1.3h2.2a1.3 1.3 0 0 1 1.3 1.3v1.5" />
      <path d="m6.9 6.8.8 11.6A1.7 1.7 0 0 0 9.4 20h5.2a1.7 1.7 0 0 0 1.7-1.6l.8-11.6" />
      <path d="M10.5 10.4v5.8M13.5 10.4v5.8" />
    </Svg>
  );
}

/** The grip on a row: what you put the pointer on to drag it. */
export function IconGrip(props: IconProps) {
  return (
    <Svg {...props}>
      {[7, 12, 17].map((y) => (
        <g key={y}>
          <circle cx="9.3" cy={y} r="1.25" fill="currentColor" stroke="none" />
          <circle cx="14.7" cy={y} r="1.25" fill="currentColor" stroke="none" />
        </g>
      ))}
    </Svg>
  );
}

export function IconInfo(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 11.2v5.4" />
      <circle cx="12" cy="7.9" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/* ── The screen itself, and one glyph per block type ───────────────────── */

/** The screen: a phone wearing a background. */
export function IconScreen(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="5.5" y="2.5" width="13" height="19" rx="2.8" />
      <circle cx="9.6" cy="7.6" r="1.3" />
      <path d="M5.6 17.2 9.5 13a2 2 0 0 1 2.9 0l4.4 4.6" />
    </Svg>
  );
}

function IconText(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 7.4V5.4h14v2" />
      <path d="M12 5.4v13.2" />
      <path d="M9.2 18.6h5.6" />
    </Svg>
  );
}

function IconButton(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="7.6" width="18" height="8.8" rx="4.4" />
      <path d="M8.4 12h7.2" />
    </Svg>
  );
}

function IconImage(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4.6" width="18" height="14.8" rx="2.6" />
      <circle cx="8.6" cy="9.6" r="1.5" />
      <path d="m3.4 17.4 4.4-4.3a2 2 0 0 1 2.8 0l3.4 3.4" />
      <path d="m13 15.2 1.9-1.9a2 2 0 0 1 2.8 0l2.9 2.9" />
    </Svg>
  );
}

function IconSpacer(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 4.8h16M4 19.2h16" />
      <path d="M12 8.4v7.2" />
      <path d="m9.8 10.6 2.2-2.2 2.2 2.2M9.8 13.4l2.2 2.2 2.2-2.2" />
    </Svg>
  );
}

function IconDivider(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 12h18" />
      <path d="M6.5 6.6h11M6.5 17.4h11" strokeOpacity="0.4" />
    </Svg>
  );
}

function IconStatus(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2.6" />
      <path d="M6.8 10h3.6M6.8 14h3.6" />
      <path d="M13.6 10h3.6M13.6 14h2.2" strokeOpacity="0.45" />
    </Svg>
  );
}

/**
 * A view a module draws inside the screen (MODULES §7.4) — a page, because
 * that is what such a block is from the customer's side: a browser chrome bar
 * over a sheet of content. One glyph for every module's blocks, on purpose:
 * nothing in `web/` knows a module by name (M6).
 */
export function IconModuleBlock(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4.6" width="18" height="14.8" rx="2.6" />
      <path d="M3 9.2h18" />
      <circle cx="6.2" cy="6.9" r="0.85" fill="currentColor" stroke="none" />
      <path d="M7.4 13h9.2M7.4 16.2h5.8" strokeOpacity="0.45" />
    </Svg>
  );
}

/** The palette, the list row and the inspector's header all draw this. */
export function BlockIcon({ type, ...props }: IconProps & { type: BlockType | "module" }) {
  switch (type) {
    case "text":
      return <IconText {...props} />;
    case "button":
      return <IconButton {...props} />;
    case "image":
      return <IconImage {...props} />;
    case "spacer":
      return <IconSpacer {...props} />;
    case "divider":
      return <IconDivider {...props} />;
    case "status":
      return <IconStatus {...props} />;
    case "module":
      return <IconModuleBlock {...props} />;
  }
}
